import * as React from "react";
import { Grid, TextField, Button, Box, Typography, CircularProgress, Snackbar, Alert } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createAndPlaceOrder } from "../../../Redux/Customers/Order/Action";
import AddressCard from "../adreess/AdreessCard";
import { useState, useEffect, useRef, useMemo } from "react";
import { getCart, loadGuestCart, clearUserCart } from "../../../Redux/Customers/Cart/Action";
import CartItem from "../Cart/CartItem";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import OrderConfirmationModal from "./OrderConfirmationModal";
import { getBoxSizeDisplay } from "../../../utils/boxPricing";

/**
 * Helper function to create a unique key for an address based on its details.
 * This is used to identify duplicate addresses.
 */
const getAddressKey = (address) => {
  return `${(address.streetAddress || '').toLowerCase().trim()}-${(address.city || '').toLowerCase().trim()}-${(address.state || '').toLowerCase().trim()}-${(address.zipCode || '').trim()}-${(address.mobile || '').replace(/\D/g, '')}`;
};

/**
 * Helper function to deduplicate addresses.
 * Keeps the first occurrence (most recently added) of each unique address.
 */
const deduplicateAddresses = (addresses) => {
  if (!addresses || addresses.length === 0) return [];

  const seen = new Set();
  return addresses.filter((address) => {
    const key = getAddressKey(address);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export default function AddDeliveryAddressForm({ handleNext }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const { auth, cart } = useSelector((store) => store);
  const [selectedAddress, setSelectedAdress] = useState(null);
  const [localAddresses, setLocalAddresses] = useState(auth.user?.addresses || []);
  const [guestEmail, setGuestEmail] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Determine if we're in guest mode
  const isGuest = !jwt;

  // Get the appropriate cart items based on mode
  const cartItems = isGuest ? cart.guestCartItems : cart.cart?.cartItems;
  const totalPrice = isGuest ? cart.guestTotalPrice : cart.cart?.totalPrice;
  const totalDiscountedPrice = isGuest
    ? cart.guestTotalDiscountedPrice
    : cart.cart?.totalDiscountedPrice;
  const totalItem = isGuest ? cart.guestTotalItem : cart.cart?.totalItem;
  const discount = isGuest
    ? (cart.guestTotalPrice - cart.guestTotalDiscountedPrice)
    : cart.cart?.discounte;

  // Track if checkout address was restored - prevents auth sync from overwriting
  const checkoutRestoredRef = useRef(false);

  // Compute unique addresses using useMemo - prevents duplicates from showing
  // The order is preserved (most recently added first)
  const uniqueAddresses = useMemo(() => {
    return deduplicateAddresses(localAddresses);
  }, [localAddresses]);

  // Load Cart Data
  useEffect(() => {
    if (jwt) {
      dispatch(getCart(jwt));
    } else {
      dispatch(loadGuestCart());
    }
  }, [jwt, dispatch]);

  // Sync addresses from auth.user when user data changes
  // This ensures addresses persist across page visits during the session
  // Addresses are already sorted newest-first from the backend
  useEffect(() => {
    const addrs = auth.user?.addresses || [];
    if (addrs.length > 0) {
      // Update local addresses with backend data (already sorted newest first from backend)
      setLocalAddresses(addrs);

      // Skip default selection if we already restored from checkout state
      if (checkoutRestoredRef.current) return;

      // Select first address (newest) as default
      const uniqueAddrs = deduplicateAddresses(addrs);
      setSelectedAdress((prev) => {
        // If there's already a selected address, check if it still exists in unique list
        if (prev) {
          const prevKey = getAddressKey(prev);
          const existsInUnique = uniqueAddrs.some(a => getAddressKey(a) === prevKey);
          if (existsInUnique) return prev;
        }
        return uniqueAddrs[0] || null;
      });
    }
  }, [auth.user?.addresses]);

  // Load guest email and address from sessionStorage if present (after login/registration)
  useEffect(() => {
    const checkoutStateStr = sessionStorage.getItem("checkoutState");
    if (checkoutStateStr && jwt) {
      try {
        const parsed = JSON.parse(checkoutStateStr);
        if (parsed.email) setGuestEmail(parsed.email);
        if (parsed.address) {
          const savedAddress = {
            firstName: parsed.address.firstName,
            lastName: parsed.address.lastName,
            streetAddress: parsed.address.streetAddress,
            city: parsed.address.city,
            state: parsed.address.state,
            zipCode: parsed.address.zipCode,
            mobile: parsed.address.mobile,
          };

          // Save address to backend so it persists across page visits
          axios.post(`${API_BASE_URL}/api/users/address`, savedAddress, {
            headers: { Authorization: `Bearer ${jwt}` }
          }).then((response) => {
            // Update Redux state with returned user data
            if (response.data) {
              dispatch({ type: "GET_USER_SUCCESS", payload: response.data });
              setLocalAddresses(response.data.addresses || []);
              // Select the most recently added address
              const savedAddresses = response.data.addresses || [];
              if (savedAddresses.length > 0) {
                setSelectedAdress(savedAddresses[savedAddresses.length - 1]);
              }
            }
            // Clear checkout state after successful save
            sessionStorage.removeItem("checkoutState");
          }).catch((error) => {
            console.error("Error saving address from checkout state:", error);
            // Fallback: still show address locally even if save fails
            const localAddr = { id: Date.now(), ...savedAddress, email: parsed.email };
            setLocalAddresses((prev) => [localAddr, ...(prev || [])]);
            setSelectedAdress(localAddr);
            sessionStorage.removeItem("checkoutState");
          });

          checkoutRestoredRef.current = true;
        }
      } catch (e) {
        console.error("Error parsing checkout state:", e);
      }
    }
  }, [jwt, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const firstName = data.get("firstName")?.trim();
    const lastName = data.get("lastName")?.trim();
    const streetAddress = data.get("address")?.trim();
    const city = data.get("city")?.trim();
    const state = data.get("state")?.trim();
    const zipCode = data.get("zip")?.trim();
    const mobile = data.get("phoneNumber")?.trim();
    // Use guestEmail state since the Contact email field is outside this form
    const email = guestEmail?.trim();

    // Basic validation
    if (!firstName || !lastName || !streetAddress || !city || !state || !zipCode || !mobile) {
      alert("Please fill in all required fields");
      return;
    }
    if (!jwt && !email) {
      alert("Please enter your email address in the Contact section above.");
      return;
    }
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert("Please enter a valid email address");
        return;
      }
    }
    const phoneRegex = /^[\d\s\-\+\(\)]{10,15}$/;
    if (!phoneRegex.test(mobile)) {
      alert("Please enter a valid phone number");
      return;
    }
    const zipRegex = /^\d{5}(-\d{4})?$|^\d{6}$/;
    if (!zipRegex.test(zipCode)) {
      alert("Please enter a valid zip/postal code");
      return;
    }

    const addressData = { firstName, lastName, streetAddress, city, state, zipCode, mobile };

    // For logged-in users: save address to backend and update state
    if (jwt) {
      try {
        // Call backend API to persist address
        const response = await axios.post(`${API_BASE_URL}/api/users/address`, addressData, {
          headers: { Authorization: `Bearer ${jwt}` }
        });

        // Update Redux state with the returned user (which includes all addresses)
        if (response.data) {
          dispatch({ type: "GET_USER_SUCCESS", payload: response.data });
          // Update local state from the new user data (sorted newest first from backend)
          setLocalAddresses(response.data.addresses || []);
          // Select the first address (newest, at index 0) as default
          const savedAddresses = response.data.addresses || [];
          if (savedAddresses.length > 0) {
            setSelectedAdress(savedAddresses[0]);
          }
        }

        event.target.reset();
      } catch (error) {
        console.error("Error saving address:", error);
        alert("Failed to save address. Please try again.");
      }
      return;
    }

    // For guests: Check if email exists and validate name match
    try {
      const checkResponse = await axios.post(`${API_BASE_URL}/auth/check-email`, {
        email: email,
        firstName: firstName,
        lastName: lastName,
      });

      const { exists, nameMatches } = checkResponse.data;

      // Save checkout state for all scenarios (address will persist after login/register)
      const checkoutState = {
        address: addressData,
        email: email,
        returnUrl: "/checkout?step=2",
        timestamp: Date.now(),
      };
      sessionStorage.setItem("checkoutState", JSON.stringify(checkoutState));

      if (exists) {
        if (nameMatches) {
          // Existing user with matching name - redirect to login
          alert("You already have an account with this email. Please sign in to continue.");
          navigate("/login");
        } else {
          // Email exists but name doesn't match - show error, don't redirect
          alert("This email is already registered with a different name. Please use a different email address.");
        }
        return;
      }

      // New user - proceed to registration
      navigate("/register");
    } catch (error) {
      console.error("Error checking email:", error);
      // On API error, fallback to register flow
      const checkoutState = {
        address: addressData,
        email: email,
        returnUrl: "/checkout?step=2",
        timestamp: Date.now(),
      };
      sessionStorage.setItem("checkoutState", JSON.stringify(checkoutState));
      navigate("/register");
    }
  };

  const handleCreateOrder = async (item) => {
    if (!item.streetAddress || !item.city || !item.state || !item.zipCode || !item.mobile) {
      alert("Selected address is incomplete. Please add a new address.");
      return;
    }
    const emailToUse = item.email || guestEmail;
    if (!jwt && !emailToUse) {
      alert("Please enter your email address in the form before proceeding.");
      return;
    }
    const addressData = {
      firstName: item.firstName,
      lastName: item.lastName,
      streetAddress: item.streetAddress,
      city: item.city,
      state: item.state,
      zipCode: item.zipCode,
      mobile: item.mobile,
    };

    if (!jwt) {
      // Guest flow: Check if email exists and validate name match
      try {
        const checkResponse = await axios.post(`${API_BASE_URL}/auth/check-email`, {
          email: emailToUse,
          firstName: item.firstName,
          lastName: item.lastName,
        });

        const { exists, nameMatches } = checkResponse.data;

        // Save checkout state for all scenarios
        const checkoutState = {
          address: addressData,
          email: emailToUse,
          returnUrl: "/checkout?step=2",
          timestamp: Date.now(),
        };
        sessionStorage.setItem("checkoutState", JSON.stringify(checkoutState));

        if (exists) {
          if (nameMatches) {
            // Existing user with matching name - redirect to login
            alert("You already have an account. Please sign in to continue.");
            navigate("/login");
          } else {
            // Email exists but name doesn't match - show error
            alert("This email is already registered with a different name. Please use a different email address.");
          }
          return;
        }

        // New user - proceed to registration
        navigate("/register");
        return;
      } catch (error) {
        console.error("Error checking email:", error);
        // On error, fallback to register flow
        const checkoutState = {
          address: addressData,
          email: emailToUse,
          returnUrl: "/checkout?step=2",
          timestamp: Date.now(),
        };
        sessionStorage.setItem("checkoutState", JSON.stringify(checkoutState));
        navigate("/register");
        return;
      }
    }
    // For logged-in users: Create and place order in one step
    setIsPlacingOrder(true);
    try {
      const orderData = await dispatch(createAndPlaceOrder({
        address: addressData,
        specialInstructions,
        jwt
      }));

      // Clear cart
      dispatch(clearUserCart(jwt));

      // Set the placed order and show modal
      setPlacedOrder(orderData);
      setShowConfirmModal(true);
      setSnackbar({
        open: true,
        message: "Order placed successfully! Confirmation email sent.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error placing order:", error);
      setSnackbar({
        open: true,
        message: "Failed to place order. Please try again.",
        severity: "error",
      });
    }
    setIsPlacingOrder(false);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    navigate("/");
  };

  const handleViewOrders = () => {
    setShowConfirmModal(false);
    navigate("/account/order");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Grid container spacing={4} sx={{ justifyContent: "space-between" }}>
      {/* Left Column: Contact, Saved Addresses, Form */}
      <Grid item xs={12} lg={6} className="space-y-6">

        {/* Contact Information Section (Emulating Miami Fruit) */}
        {!jwt ? (
          <Box className="border-b pb-5">
            <div className="flex justify-between items-center mb-4">
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Please Sign in to Continue Your Order
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set("login", "true");
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              >
                Sign in
              </Button>
            </div>
            <TextField
              id="email-guest"
              label="Email"
              fullWidth
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="Email"
              variant="outlined"
              size="small"
            />
          </Box>
        ) : (
          <Box className="border-b pb-5">
            <Typography variant="h6" className="font-semibold mb-2">
              Contact
            </Typography>
            <Typography variant="body1">
              {auth.user?.email}
            </Typography>
          </Box>
        )}


        {/* Saved Addresses Section - Compact & Scrollable */}
        {jwt && (
          <Box className="border rounded-md shadow-sm p-4 bg-gray-50">
            <Typography variant="subtitle1" className="font-semibold mb-3">Saved Addresses</Typography>
            {uniqueAddresses?.length > 0 ? (
              /* Scrollable Container with reduced height - uses uniqueAddresses to prevent duplicates */
              <Box className="overflow-y-auto pr-2" sx={{ maxHeight: "200px" }}>
                {uniqueAddresses.map((item, index) => {
                  // Use address key for comparison to handle addresses with different IDs but same content
                  const isSelected = selectedAddress && getAddressKey(selectedAddress) === getAddressKey(item);
                  return (
                    <div key={item.id || `address-${index}`}
                      onClick={() => setSelectedAdress(item)}
                      className={`p-3 mb-2 border rounded cursor-pointer transition-colors ${isSelected ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-white hover:bg-gray-100'}`}>
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="selectedAddress"
                          checked={isSelected}
                          readOnly
                          className="mt-1 accent-purple-600"
                        />
                        <div className="flex-1 text-sm">
                          <p className="font-semibold">{item.firstName} {item.lastName}</p>
                          <p className="text-gray-600">{item.streetAddress}, {item.city}, {item.state} {item.zipCode}</p>
                          <p className="text-gray-600">{item.mobile}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No saved addresses found.</Typography>
            )}
          </Box>
        )}

        {/* Shipping Address Form */}
        <Box>
          <Typography variant="h6" className="font-semibold mb-4">Shipping address</Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  required
                  name="firstName"
                  label="First name"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  name="lastName"
                  label="Last name"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="company"
                  label="Company (optional)"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  name="address"
                  label="Address"
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Address"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="apartment"
                  label="Apartment, suite, etc. (optional)"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  required
                  name="city"
                  label="City"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  required
                  name="state"
                  label="State"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  required
                  name="zip"
                  label="PIN code"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  name="phoneNumber"
                  label="Phone"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }} onClick={() => navigate("/cart")}>
                    &lt; Return to cart
                  </Typography>
                  <Button
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ py: 1.5, px: 4 }}
                  >
                    Save Address
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {/* Right Column: Order Summary (Sticky) */}
      <Grid item xs={12} lg={6} sx={{ position: { lg: 'sticky' }, top: '2rem', height: 'fit-content' }}>
        <Box className="bg-gray-50 p-5 rounded-md border">
          {cartItems?.map((item) => (
            <Box key={item.id} className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className="relative border rounded w-16 h-16 overflow-visible">
                  <img src={item.product.imageUrl} alt={item.product.title} className="w-full h-full object-cover rounded" />
                  <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-gray-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {item.quantity}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium">{item.product.title}</p>
                  <p className="text-xs text-gray-500">{getBoxSizeDisplay(item.size)}</p>
                </div>
              </div>
              <p className="text-sm font-medium">${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}</p>
            </Box>
          ))}

          <hr className="my-4" />

          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-medium text-black">${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : (totalPrice || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span className="text-xs italic text-blue-600">Calculated at the Time of Delivery</span>
            </div>
            <div className="flex justify-between">
              <span>Discount</span>
              <span className="text-green-600">-${typeof discount === 'number' ? discount.toFixed(2) : (discount || 0)}</span>
            </div>
          </div>

          <hr className="my-4" />

          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total</span>
            <span>${typeof totalDiscountedPrice === 'number' ? totalDiscountedPrice.toFixed(2) : (totalDiscountedPrice || 0)}</span>
          </div>

          {/* Special Instructions */}
          {jwt && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", opacity: 0.7 }}>
                SPECIAL INSTRUCTIONS (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add any delivery notes, gate codes, or special requests..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                inputProps={{ maxLength: 512 }}
              />
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "right", opacity: 0.6 }}>
                {specialInstructions.length}/512 characters
              </Typography>
            </Box>
          )}
        </Box>

        {/* Place Order button - outside the summary box */}
        {jwt && selectedAddress && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={isPlacingOrder}
              sx={{
                py: 1.5,
                px: 4,
                textTransform: 'uppercase',
                fontWeight: 'bold',
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)",
                },
              }}
              onClick={() => handleCreateOrder(selectedAddress)}
            >
              {isPlacingOrder ? <CircularProgress size={24} color="inherit" /> : "Place Order"}
            </Button>
          </Box>
        )}
      </Grid>

      {/* Order Confirmation Modal */}
      <OrderConfirmationModal
        open={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleViewOrders}
        order={placedOrder}
        isLoading={false}
        specialInstructions={specialInstructions}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
